import {
  identity,
  emptyObject,
  union as fpUnion,
} from '@packages/utilities/fp/utils';
import {unionWith} from '@packages/utilities/key-map';
import {type Json, matchJson, getString} from '@packages/utilities/json';
import {applyBrand, type BrandedType} from '@packages/utilities/type-safety';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
// Because we use PluginName as the qualifier for all qualified values, we need
// to keep it in this file in order to use it below.
export type PluginName = string;
export const pluginName: (arg1: string) => PluginName = identity;
export const pluginNameToString: (arg1: PluginName) => string = identity;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type Identifier = string;
export const identifier: (arg1: string) => Identifier = identity;
export const identifierToString: (arg1: Identifier) => string = identity;
export const isValidIdentifier = (name: string): boolean =>
  /^[a-z_$][a-z0-9_$]*$/gi.test(name);

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type TypeName = string;
export const typeName: (arg1: string) => TypeName = identity;
export const typeNameToString: (arg1: TypeName) => string = identity;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type IconName = string;
export const iconName: (arg1: string) => IconName = identity;

export type SiteModuleDependencyName = BrandedType<
  'SiteModuleDependencyName',
  string
>;
export const siteModuleDependencyName = (
  dependencyName: string
): SiteModuleDependencyName =>
  applyBrand<SiteModuleDependencyName>(dependencyName);

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * An identifier (i.e. a name) of some kind that is scoped to a specific plugin.
 */
export type Qualified<name> = [PluginName, name];

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
type QualifiedMapData<key extends string, value> = Record<
  PluginName,
  Record<key, value>
>;

export class QualifiedMap<key extends string, value> {
  static empty<k extends string = never, v = never>(): QualifiedMap<k, v> {
    return emptyQualifiedMap as QualifiedMap<k, v>;
  }

  static deserialize<K extends string, serializedValue extends Json>(
    str: SerializedQualifiedMap<K, serializedValue>
  ): QualifiedMap<K, serializedValue> {
    return deserialize(str);
  }

  _data: QualifiedMapData<key, value>;

  constructor(data: QualifiedMapData<key, value> = {}) {
    this._data = data;
  }

  serialize<serializedValue extends Json>(): SerializedQualifiedMap<
    key,
    serializedValue
  > {
    return serialize<key, value, serializedValue>(this);
  }

  // Useful mainly for internals/debugging/jest snapshots; sort of an alias for
  // serialize, but produces more human-readable output.
  toJSON(): QualifiedMapData<key, value> {
    return this._data;
  }

  byPlugin(): Record<PluginName, Record<key, value>> {
    return this._data;
  }

  get(qname: Qualified<key>): value | undefined {
    const namespace = qname[0];
    const name = qname[1];
    // Only return explicitly stored entries, excluding inherited properties.
    if (!Object.hasOwn(this._data, namespace)) return undefined;
    const inner = this._data[namespace];
    return inner && Object.hasOwn(inner, name) ? inner[name] : undefined;
  }

  set(qname: Qualified<key>, val: value): QualifiedMap<key, value> {
    if (this.get(qname) === val) return this;
    const [namespace, name] = qname;
    const currentValues: Record<key, value> =
      this._data[namespace] ?? emptyObject;
    return new QualifiedMap({
      ...this._data,
      [namespace]: {
        ...currentValues,
        [name]: val,
      },
    });
  }

  delete(qname: Qualified<key>): QualifiedMap<key, value> {
    if (this.get(qname) == null) return this;

    const [namespace, name] = qname;
    const {[name]: __discarded__, ...updatedNamespace} = this._data[namespace];

    return new QualifiedMap({
      ...this._data,
      // Type safety alert! We need to cast updatedNamespace for TS to
      // accept object extracted from Record<key, value> with spread syntax
      // as Record<key, value>.
      [namespace]: updatedNamespace as Record<key, value>,
    });
  }

  union(other: QualifiedMap<key, value>): QualifiedMap<key, value> {
    const updatedData = unionWith<Record<key, value>>(fpUnion)(other._data)(
      this._data
    );

    if (updatedData === this._data) return this;
    if (updatedData === other._data) return other;

    return new QualifiedMap(updatedData);
  }

  filter(
    f: (arg1: value, arg2: Qualified<key>) => boolean
  ): QualifiedMap<key, value> {
    let updated = false;
    const updatedMap = new QualifiedMap(
      this.reduce((acc, val, name) => {
        if (f(val, name)) {
          acc.set_mut(name, val);
        } else {
          updated = true;
        }
        return acc;
      }, new MutableQualifiedMap<key, value>())._data
    );
    if (!updated) return this;
    return updatedMap;
  }

  forEach(f: (arg1: value, arg2: Qualified<key>) => void): void {
    for (const ns in this._data) {
      const namespace = pluginName(ns);
      const inner = this._data[namespace];
      for (const name_ in inner) {
        const name: key = name_ as any;
        f(inner[name], [namespace, name]);
      }
    }
  }

  map<T>(f: (arg1: value, arg2: Qualified<key>) => T): QualifiedMap<key, T> {
    let updated = false;
    const nextMap = this.reduce((acc, val, name) => {
      const value = f(val, name);
      acc.set_mut(name, value);
      if (value !== (val as unknown as T)) {
        updated = true;
      }
      return acc;
    }, new MutableQualifiedMap<key, T>())._data;

    if (updated) {
      return new QualifiedMap(nextMap);
    }
    return this as unknown as QualifiedMap<key, T>;
  }

  reduce<accumulator>(
    reducer: (
      arg1: accumulator,
      arg2: value,
      arg3: Qualified<key>
    ) => accumulator,
    init: accumulator
  ): accumulator {
    return Object.keys(this._data).reduce((acc, namespace) => {
      const inner = this._data[namespace] ?? emptyObject;
      return Object.keys(inner).reduce(
        // @ts-expect-error - TS18048 - 'inner' is possibly 'undefined'.
        (innerAcc, name) => reducer(innerAcc, inner[name], [namespace, name]),
        acc
      );
    }, init);
  }

  reduceAtPluginName<accumulator>(
    pluginNamePath: PluginName,
    reducer: (arg1: accumulator, arg2: value, arg3: key) => accumulator,
    init: accumulator
  ): accumulator {
    const plugin = this._data[pluginNamePath];
    let result = init;
    if (!plugin) return init;

    for (const nameKey in plugin) {
      const name = nameKey as key;
      result = reducer(result, plugin[name], name);
    }

    return result;
  }

  getAllPluginNames(): Array<PluginName> {
    return Object.keys(this._data);
  }

  find(
    predicate: (arg1: value, arg2: Qualified<key>) => boolean
  ): value | undefined {
    for (const ns in this._data) {
      const namespace = pluginName(ns);
      const values = this._data[namespace];
      for (const name in values) {
        if (predicate(values[name], qualify(namespace, name))) {
          return values[name];
        }
      }
    }
  }

  has(qname: Qualified<key>): boolean {
    return this.get(qname) != null;
  }

  size(): number {
    return Object.keys(this._data).reduce(
      (acc, namespace) =>
        acc + Object.keys(this._data[namespace] ?? emptyObject).length,
      0
    );
  }

  /**
   * Returns a mutable copy the QualifiedMap
   */
  asMutableCopy(): MutableQualifiedMap<key, value> {
    const mutableMap = new MutableQualifiedMap<key, value>();
    this.forEach((value, key) => mutableMap.set_mut(key, value));
    return mutableMap;
  }
}

export class MutableQualifiedMap<
  key extends string,
  value,
> extends QualifiedMap<key, value> {
  /**
   * If true, this MutableQualifiedMap instance cannot be mutated
   */
  #frozen: boolean;

  constructor(data: QualifiedMapData<key, value> = {}) {
    super(data);
    this.#frozen = false;
  }

  /**
   * Returns a new QualifiedMap with the same _data
   * and freezes this instance so that it cannot be mutated anymore.
   */
  asNonMutableCopy(): QualifiedMap<key, value> {
    this._freeze();
    return new QualifiedMap(this._data);
  }

  /**
   * Internal method to freeze this instance
   * so that it cannot be mutated anymore.
   */
  _freeze(): void {
    this.#frozen = true;
  }

  _isFrozen(): boolean {
    return this.#frozen;
  }

  set_mut(qname: Qualified<key>, val: value): void {
    if (this.#frozen) {
      throw new Error(
        `Attempted to call mutating method \`set_mut\` on a frozen MutableQualifiedMap`
      );
    }
    if (this.get(qname) === val) return;
    const [namespace, name] = qname;
    // Assigning to __proto__ can change an object's prototype instead of storing an entry.
    if (namespace === '__proto__' || name === '__proto__') return;
    // Create a namespace bucket if absent, never write into an inherited object.
    if (!Object.hasOwn(this._data, namespace) || !this._data[namespace]) {
      this._data[namespace] = {} as Record<key, value>;
    }
    this._data[namespace][name] = val;
  }

  delete_mut(qname: Qualified<key>): void {
    if (this.#frozen) {
      throw new Error(
        `Attempted to call mutating method \`delete_mut\` on a frozen MutableQualifiedMap`
      );
    }
    if (this.get(qname) == null) return;

    const [namespace, name] = qname;
    const {[name]: __discarded__, ...updatedNamespace} = this._data[namespace];

    this._data[namespace] = updatedNamespace as Record<key, value>;
  }
}

const emptyQualifiedMap: QualifiedMap<any, any> = new QualifiedMap(emptyObject);

/**
 * Return the `PluginName` part of a `Qualified` name
 */
export const getPluginName = <n>(name: Qualified<n>): PluginName => name[0];

/**
 * Return the plugin-scoped unique name part of a `Qualified` name
 */
export const getName = <n>(name: Qualified<n>): n => name[1];

/**
 * Return a human-readable string representation of a `Qualified` name. Useful
 * for logging and error reporting.
 *
 * **Do not construct a new `Qualified` value from this string.**
 */
// eslint-disable-next-line unused-imports/no-unused-vars
export const toHumanReadableString = <n extends string>([
  _pluginName,
  _identifier,
]: [any, any]): string => {
  return [
    // Prefix our plugin name with the unqualified namespace if we need to
    // bracket it:
    isValidIdentifier(_pluginName)
      ? _pluginName
      : `::[${JSON.stringify(_pluginName)}]`,
    isValidIdentifier(_identifier)
      ? _identifier
      : `[${JSON.stringify(_identifier)}]`,
  ].join('::');
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type SerializedQualifiedMap<
  // eslint-disable-next-line unused-imports/no-unused-vars
  key extends string,
  // eslint-disable-next-line unused-imports/no-unused-vars
  value extends Json,
> = string;

export const equal = <a>(name1: Qualified<a>, name2: Qualified<a>) =>
  name1[0] === name2[0] && name1[1] === name2[1];

// This is a temporary function where we can update callsites of a thing that
// will be converted to a qualified system but will still fall back to the
// string value of its `name`, for example an `componentName`
export function qualify<n>(plugin: PluginName, name: n): Qualified<n> {
  return [plugin, name];
}

const returnNull = (
  _:
    | null
    | number
    | string
    | boolean
    | {
        [key: string]: Json;
      }
) => null;

export const parseQualified = <a>(
  buildName: (arg1: string) => a
): ((arg1: Json) => null | Qualified<a>) =>
  matchJson({
    Null: returnNull,
    Boolean: returnNull,
    Number: returnNull,
    String: returnNull,
    Array: ([plugin, name]) => {
      if (plugin == null || name == null) return null;
      const pluginString = getString(plugin);
      const nameString = getString(name);
      if (pluginString == null || nameString == null) return null;
      return qualify(pluginName(pluginString), buildName(nameString));
    },
    Object: returnNull,
  });

const serialize = <key extends string, value, serializedValue extends Json>(
  qmap: QualifiedMap<key, value>
): SerializedQualifiedMap<key, serializedValue> => JSON.stringify(qmap._data);

const deserialize = <key extends string, value extends Json>(
  str: SerializedQualifiedMap<key, value>
): QualifiedMap<key, value> => new QualifiedMap(JSON.parse(str));
